/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const loadingMessage = defineMessage({
	id: 'servicedesk-resources-calendar.calendar-skeleton.loading',
	defaultMessage: 'Loading',
	description: 'Accessible loading message for screen readers while calendar is loading',
});

const ROWS = 7;
const COLUMNS = 7;

const AGENT_CELL_HEIGHT = '64px';
const CALENDAR_CELL_HEIGHT = '37px';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		width: '100%',
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.100'),
		alignItems: 'center',
	},
	spacer: {
		width: '0px',
		paddingLeft: token('space.100'),
	},
});

const CalendarSkeletonRow = () => (
	<Flex xcss={styles.row}>
		<Skeleton height={AGENT_CELL_HEIGHT} width="100%" />
		<Box xcss={styles.spacer} />
		{Array.from({ length: COLUMNS }).map((_, index) => (
			<Skeleton key={index} height={CALENDAR_CELL_HEIGHT} width="100%" />
		))}
	</Flex>
);

export type CalendarSkeletonProps = {
	/** Number of rows to display (default: 7) */
	rowCount?: number;
};

export const CalendarSkeleton = ({ rowCount = ROWS }: CalendarSkeletonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container} testId="resources-calendar.skeleton">
			<VisuallyHidden>{formatMessage(loadingMessage)}</VisuallyHidden>
			{Array.from({ length: rowCount }).map((_, index) => (
				<CalendarSkeletonRow key={index} />
			))}
		</Box>
	);
};

export default CalendarSkeleton;
